import React, { useState, useEffect, useRef } from 'react'
import { Toast, toast } from '@opallabs/lapidary'

interface AdobePDFViewerProps {
  fileUrl: string
  fileName: string
  fileId: string
  userProfile: {
    name: string
    firstName: string
    lastName: string
    email: string
  }
  onAnnotationAdded: (annotation: any) => void
  onAnnotationUpdated: (annotation: any) => void
  onAnnotationDeleted: (annotationId: string) => void
  getExistingAnnotations: () => Promise<any[]>
  onShowPdfAnnotation: (callback: (annotationId: string) => void) => void
}

const showErrorToast = (message: string, title: string) => {
  toast((props) => <Toast {...props} message={message} title={title} />, {
    type: 'error',
    toastId: 'pdf-preview-csv-export-error-toast',
    autoClose: 5000,
    closeOnClick: true,
  })
}

const isValidAnnotation = (data: any): boolean => {
  return data && typeof data === 'object' && data.id
}

const isValidAnnotationWithCreator = (data: any): boolean => {
  return isValidAnnotation(data) && data.creator
}

export const AdobePDFViewer: React.FC<AdobePDFViewerProps> = ({
  fileUrl,
  fileName,
  fileId,
  userProfile,
  onAnnotationAdded,
  onAnnotationUpdated,
  onAnnotationDeleted,
  getExistingAnnotations,
  onShowPdfAnnotation,
}) => {
  const [annotationManager, setAnnotationManager] = useState<any>(null)
  const loadedAnnotationIdsRef = useRef(new Set<string>())

  useEffect(() => {
    const initializeViewer = async () => {
      const adobeDCView = new window.AdobeDC.View({
        clientId: window.ADOBE_PDF_PREVIEW_CLIENT_ID,
        divId: 'adobe-dc-view',
      })

      adobeDCView.registerCallback(
        window.AdobeDC.View.Enum.CallbackType.GET_USER_PROFILE_API,
        () => {
          return Promise.resolve({
            code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
            data: { userProfile },
          })
        }
      )

      const previewFilePromise = adobeDCView.previewFile(
        {
          content: { location: { url: fileUrl } },
          metaData: { fileName, id: fileId },
        },
        {
          enableAnnotationAPIs: true,
          includePDFAnnotations: true,
          showSaveButton: true,
        }
      )

      const viewer = await previewFilePromise
      const annotationManager = await viewer.getAnnotationManager()
      setAnnotationManager(annotationManager)

      const existingAnnotations = await getExistingAnnotations()
      if (existingAnnotations && existingAnnotations.length > 0) {
        await annotationManager.addAnnotations(existingAnnotations)
      }

      existingAnnotations.forEach((annotation) => {
        loadedAnnotationIdsRef.current.add(annotation.id)
      })

      annotationManager.registerEventListener(
        (event: any) => {
          // Use setTimeout to isolate from Adobe SDK call stack and prevent
          // Adobe's internal errors (e.g. placeCaretAtEnd focus issues) from breaking our handlers
          setTimeout(() => {
            try {
              switch (event.type) {
                case 'ANNOTATION_ADDED':
                  if (
                    isValidAnnotation(event.data) &&
                    !loadedAnnotationIdsRef.current.has(event.data.id)
                  ) {
                    onAnnotationAdded(event.data)
                    loadedAnnotationIdsRef.current.add(event.data.id)
                  }
                  break
                case 'ANNOTATION_UPDATED':
                  if (!isValidAnnotationWithCreator(event.data)) {
                    break
                  }
                  if (event.data.creator.id != userProfile.email) {
                    showErrorToast(
                      "You cannot edit another user's annotation.",
                      'Cannot Edit Annotation'
                    )
                    break
                  }
                  onAnnotationUpdated(event.data)
                  break
                case 'ANNOTATION_DELETED':
                  if (!isValidAnnotationWithCreator(event.data)) {
                    break
                  }
                  if (event.data.creator.id != userProfile.email) {
                    showErrorToast(
                      "You cannot delete another user's annotation.",
                      'Cannot Delete Annotation'
                    )
                    break
                  }
                  onAnnotationDeleted(event.data.id)
                  break
              }
            } catch {
              // Silently handle any errors to prevent breaking annotation functionality
            }
          }, 0)
        },
        {
          listenOn: [
            'ANNOTATION_ADDED',
            'ANNOTATION_UPDATED',
            'ANNOTATION_DELETED',
          ],
        }
      )
    }

    initializeViewer()
  }, [
    fileUrl,
    fileName,
    fileId,
    userProfile,
    getExistingAnnotations,
    onAnnotationAdded,
    onAnnotationUpdated,
    onAnnotationDeleted,
  ])

  useEffect(() => {
    const handleShowPdfAnnotation = (annotationId: string) => {
      if (annotationManager) {
        annotationManager.setConfig({ showCommentsPanel: true })
        annotationManager.selectAnnotation(annotationId)
      }
    }

    onShowPdfAnnotation(handleShowPdfAnnotation)
  }, [annotationManager, onShowPdfAnnotation])

  return (
    <div id="pdf-viewer-container">
      <div id="adobe-dc-view"></div>
    </div>
  )
}
